"use client"
import { useState, useEffect, useCallback } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useResponsive } from "@src/hooks"
import ProductModel from "@src/models/product"
import FavoriteIcon from "./favorite-Icon"
import ImageCarouselDesktop from "./image-carousel-desktop"
import ImageCarouselMobile from "./image-carousel-mobile"
import InfoAndInventory from "./info-and-inventory"
import SizeModal from "./size-modal"
import UpcomingSalePanel from "./UpcomingSalePanel"
import BadgeProductType from "./badge-type"
import ProductSkeleton from "./product-skeleton"
import { useLayoutContext } from "@src/layouts/provider"
import { useTranslation } from "react-i18next"

type Props = {
  isModal?: boolean
  detail: ProductModel
  fetchingId: boolean
}

const ProductDetail = (props: Props) => {
  const { isModal, detail, fetchingId } = props
  const { onSaleColor } = useLayoutContext()
  const { t } = useTranslation()
  const { isMobile } = useResponsive()
  const [selectedImage, setSelectedImage] = useState<string>("")
  const [openSizeModal, setOpenSizeModal] = useState(false)

  const [selectedPrice, setSelectedPrice] = useState<{
    id: number
    price: number
  } | null>(null)
  const handleOriginalPriceChange = useCallback(
    (price: number) => {
      setSelectedPrice({ id: detail?.id, price })
    },
    [detail?.id]
  )
  const originalPrice =
    selectedPrice?.id === detail?.id
      ? selectedPrice?.price
      : detail?.original_price
  const upcoming = (detail?.flash_sales || []).find(
    s => s.status === "upcoming" && s.from && s.to
  )
  const hasActive = (detail?.flash_sales || []).some(s => s.status === "active")
  const salePanel =
    upcoming && !hasActive && originalPrice ? (
      <UpcomingSalePanel
        isPopup={!!isModal}
        compact={!!isModal}
        originalPrice={originalPrice}
        futurePrice={detail?.promotion_price ?? originalPrice}
        discountPercent={Number(upcoming.discount_percent || 0)}
        from={upcoming.from!}
        to={upcoming.to}
      />
    ) : null

  useEffect(() => {
    if (isModal) return
    window.scrollTo(0, 0)
  }, [detail?.id, isModal])

  const handleShowSizeModal = (e?: {
    preventDefault?: () => void
    stopPropagation?: () => void
  }) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setOpenSizeModal(true)
  }

  return (
    <Box
      sx={{
        position: "relative",
        height: isModal ? "auto" : "100%",
        maxHeight: isModal ? "100%" : undefined,
        minHeight: isModal ? { xs: 0, md: 550 } : 550,
        minWidth: {
          xs: "auto",
          lg: "960px"
        },
        display: isModal ? "flex" : "block",
        flexDirection: "column",
        overflow: isModal ? "hidden" : undefined
      }}
    >
      <ProductSkeleton loading={fetchingId} overlay />
      <Box
        position="relative"
        sx={{
          flex: isModal ? "0 1 auto" : undefined,
          minHeight: 0,
          maxHeight: isModal ? "100%" : undefined,
          display: isModal ? "flex" : undefined,
          flexDirection: "column",
          overflow: isModal ? "hidden" : undefined
        }}
      >
        <Stack
          mt={isModal ? { xs: 0.5, md: 2 } : 2}
          position="relative"
          flexDirection={{
            xs: "column",
            md: "row"
          }}
          gap={{
            xs: isModal ? "10px" : "16px",
            md: "32px"
          }}
          sx={{
            flex: isModal ? "0 1 auto" : undefined,
            minHeight: 0
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: {
                xs: 8,
                md: 4
              },
              left: {
                xs: 8,
                md: 4
              },
              zIndex: 99
            }}
          >
            <BadgeProductType detail={detail} onSaleColor={onSaleColor} />
          </Box>
          <Box
            width="100%"
            height="auto"
            flexShrink={0}
            display={{ xs: "block", md: "none" }}
          >
            <Box sx={{ position: "relative" }}>
              <ImageCarouselMobile
                isLoading={fetchingId}
                isModal={isModal}
                name={detail?.name}
                detail={detail}
                selectedImage={selectedImage}
              />
              {salePanel && !isModal && !fetchingId && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: 12,
                    zIndex: 3
                  }}
                >
                  {salePanel}
                </Box>
              )}
            </Box>
            {!isModal && (
              <Stack
                display={{
                  xs: "flex",
                  md: "none"
                }}
                alignItems="center"
                mt={1}
                mb={-1}
              >
                <Box
                  sx={{
                    cursor: "pointer",
                    paddingRight: "12px"
                  }}
                >
                  <FavoriteIcon productId={detail?.id} slug={detail?.slug} />
                </Box>
                <Typography variant="body1">{t("add_to_wishlist")}</Typography>
              </Stack>
            )}
          </Box>
          <Box
            width="55%"
            maxWidth="55%"
            display={{ xs: "none", md: "block" }}
            sx={{ position: "relative" }}
          >
            <ImageCarouselDesktop
              detail={detail}
              selectedImage={selectedImage}
              overlay={!isModal && !fetchingId ? salePanel : null}
            />
          </Box>
          <Box
            width={{
              xs: "100%",
              md: "45%"
            }}
            maxWidth={{
              xs: "100%",
              md: "45%"
            }}
            sx={{
              flex: isModal && !isMobile ? 1 : undefined,
              minHeight: 0,
              display: isModal ? "flex" : undefined,
              flexDirection: "column"
            }}
          >
            <InfoAndInventory
              isMobile={isMobile}
              isModal={!!isModal}
              detail={detail}
              afterPrice={isModal && !fetchingId ? salePanel : null}
              onOriginalPriceChange={handleOriginalPriceChange}
              onSelectedImage={setSelectedImage}
              onOpenSizeModal={handleShowSizeModal}
            />
          </Box>
        </Stack>

        <Stack
          display={{
            xs: "none",
            md: "flex"
          }}
          alignItems="center"
          mt={1.5}
        >
          <Box
            sx={{
              cursor: "pointer",
              backgroundColor: "white",
              padding: "4px",
              marginRight: "12px",
              borderRadius: "50%"
            }}
          >
            <FavoriteIcon productId={detail?.id} slug={detail?.slug} />
          </Box>
          <Typography variant="body1">{t("add_to_wishlist")}</Typography>
        </Stack>
      </Box>
      <SizeModal
        imgUrl={detail?.size_image || ""}
        images={detail?.size_images}
        open={openSizeModal}
        onClose={() => setOpenSizeModal(false)}
      />
    </Box>
  )
}

export default ProductDetail
